import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    DKLabel,
    DKButton,
    DKInput,
    DKCheckMark,
    INPUT_TYPE,
    INPUT_VIEW_DIRECTION,
    showLoader,
    removeLoader,
    showToast,
    TOAST_TYPE,
    showAlert
} from "deskera-ui-library";
import UserPermissionService from "./UserPermissionService";
import { isEmpty } from "../import_export/utility/ImportUtility";
import { ACTION, MODULE } from "./PermissionUtils";

function CreateRole(props) {
    const { t } = useTranslation();

    const [fetchingDependencies, setFetchingDependencies] = useState(true);
    const [nameInput, setNameInput] = useState({
        id: "nameInput",
        title: t("Role Title"),
        type: INPUT_TYPE.TEXT,
        required: true,
        canValidate: false
    });
    const [modules, setModules] = useState(null);
    const [role, setRole] = useState(null);
    const [toggleAllPermissions, setToggleAllPermissions] = useState(null);

    useEffect(() => {
        if (UserPermissionService.ROLE_DATA) {
            setRole(UserPermissionService.ROLE_DATA);
            UserPermissionService.ROLE_DATA = null;
        }

        fetchDependencies();
    }, []);

    useEffect(() => {
        if (!fetchingDependencies) {
            if (role) {
                setRoleData(role);
            } else {
                if (Array.isArray(modules)) {
                    const updatedModules = getFilterEmployeePermissions([...modules]);
                    setModules(updatedModules);
                }
            }
        }
    }, [fetchingDependencies, role]);

    const fetchDependencies = async () => {
        showLoader();
        if (Array.isArray(props.modules)) {
            let modules = props.modules.map((module, index) => {
                const permissions = module.permissions.map((action) => {
                    return {
                        key: action,
                        title: UserPermissionService.getActionName(action, module),
                        selected: false
                    };
                });

                return {
                    ...module,
                    selected: index === 0,
                    permissions: [
                        {
                            key: "SELECT_ALL",
                            title: "Select All",
                            selected: false
                        },
                        ...permissions
                    ]
                };
            });

            // Remove duplicate modules
            const modulesMap = new Map();
            for (const module of modules) {
                modulesMap.set(module.key, module);
            }

            modules = Array.from(modulesMap.values());
            setModules(modules);
        }

        removeLoader();
        setFetchingDependencies(false);
    }

    const getFilterEmployeePermissions = (modules) => {
        if (Array.isArray(modules)) {
            modules.forEach((module) => {
                if (module.key === MODULE.EMPLOYEE) {
                    module.permissions = module.permissions.filter((action) => {
                        return action.key === "SELECT_ALL" || action.key === ACTION.VIEW || action.key === ACTION.EXPORT;
                    });
                }
            });
        }

        return modules;
    }

    const getIsAllModuleSelected = (modules) => {
        if (Array.isArray(modules)) {
            let isAllModuleSelected = true;
            [...modules].forEach((module) => {
                module.permissions.forEach((action) => {
                    if (!action.selected) {
                        isAllModuleSelected = false;
                    }
                });
            });
            return isAllModuleSelected;
        }

        return false;
    }

    const setRoleData = (roleData) => {
        setNameInput({
            ...nameInput,
            value: roleData.name
        });

        let updatedModules;
        if (roleData.isDefault) {
            updatedModules = [...modules].map((m) => {
                const permissions = m.permissions.map((action) => {
                    return {
                        ...action,
                        selected: true
                    };
                });

                return {
                    ...m,
                    permissions: permissions
                };
            });
        } else {
            let isAllPermissionsSelected = !!roleData.accessToAllModules;
            updatedModules = [...modules].map((m) => {
                let permissions = m.permissions;
                if (isAllPermissionsSelected) {
                    permissions = permissions.map((a) => {
                        return {
                            ...a,
                            selected: true
                        };
                    });
                } else {
                    const module = roleData.modules.find((module) => m.key === module.key);
                    permissions = m.permissions;
                    if (module) {
                        let isAllActionSelected = true;
                        permissions = m.permissions.map((action) => {
                            const selected = module.permissions.includes(action.key) || roleData.isDefault;
                            if (!selected && action.key !== "SELECT_ALL") {
                                isAllActionSelected = false;
                            }
        
                            return {
                                ...action,
                                selected: selected
                            };
                        });
        
                        permissions.forEach((a) => {
                            if (a.key === "SELECT_ALL") {
                                a.selected = isAllActionSelected;
                            }
                        });
                    }
                }

                return {
                    ...m,
                    permissions: permissions
                };
            });
        }

        if (!(roleData.isDefault && roleData.name === "Admin") && !roleData.isSystemRole) {
            updatedModules = getFilterEmployeePermissions(updatedModules);
        }

        setToggleAllPermissions(getIsAllModuleSelected(updatedModules));
        setModules(updatedModules);
    }

    const onAllPermissionToggle = (toggleAllPermissions) => {
        if (Array.isArray(modules)) {
            const updatedModules = [...modules].map((module) => {
                const permissions = module.permissions.map((action) => {
                    return { ...action, selected: toggleAllPermissions};
                });
                return { ...module, permissions: permissions };
            });

            setModules(updatedModules);
        }
    }

    const onModuleSelect = (module) => {
        const updatedModules = [...modules].map((m) => {
            return { ...m, selected: module.key === m.key };
        });

        setModules(updatedModules);
    }

    const showAndUpdateDepsModule = (updatedModules, depsModules) => {
        if (depsModules.length > 0) {
            const actualDeps = [];
            depsModules.forEach((dm) => {
                const module = updatedModules.find((m) => m.key === dm);
                if (module) {
                    const viewAction = module.permissions.find((a) => a.key === "VIEW");
                    if (!viewAction.selected) {
                        actualDeps.push(dm);
                    }
                }
            });

            if (actualDeps.length > 0) {
                const actualUpdatedDeps = [];
                actualDeps.forEach((ad) => {
                    updatedModules.forEach((m) => {
                        if (m.key === ad) {
                            m.permissions.forEach((a) => {
                                if (a.key === "VIEW") {
                                    a.selected = true;
                                    actualUpdatedDeps.push(m.name);
                                }
                            });
                        }
                    });
                });

                if (actualUpdatedDeps.length > 0) {
                    showAlert(
                        "Alert",
                        `Provided necessary view access to below modules in order to work this module properly.
                        <br \>
                        <br \>
                        ${actualUpdatedDeps.join(', ')}`
                    );
                }
            }
        }
    }

    const onPermissionToggle = (action) => {
        const updatedModules = [...modules].map((m) => {
            let permissions = [ ...m.permissions ];
            if (m.selected) {
                if (action.key === "SELECT_ALL") {
                    permissions = permissions.map((a) => {
                        return { ...a, selected: !action.selected };
                    });
                } else {
                    permissions = permissions.map((a) => {
                        if (a.key === action.key) {
                            return { ...a, selected: !a.selected };
                        }

                        return { ...a };
                    });
                }

                // Auto select View if any other permission is given
                const viewAction = permissions.find((a) => a.key === "VIEW");
                const anyNonViewSelectedAction = permissions.find((a) => a.key !== "VIEW" && a.selected);
                if (!viewAction?.selected && anyNonViewSelectedAction) {
                    permissions.forEach((a) => {
                        if (a.key === "VIEW") {
                            a.selected = true;
                        }
                    });
                }

                // Auto select SELECT_ALL if all permissions are selected
                // const selectAllAction = permissions.find((a) => a.key === "SELECT_ALL");
                const anyNonSelectedAction = permissions.find((a) => a.key !== "SELECT_ALL" && !a.selected);
                permissions.forEach((a) => {
                    if (a.key === "SELECT_ALL") {
                        a.selected = !anyNonSelectedAction;
                    }
                });
            }

            return { ...m, permissions: permissions };
        });

        const selectedModule = updatedModules.find((m) => m.selected);
        if (selectedModule) {
            const anySelectedAction = selectedModule.permissions.find((a) => a.key !== "SELECT_ALL" && a.selected);
            if (anySelectedAction && Array.isArray(selectedModule.deps)) {
                showAndUpdateDepsModule(updatedModules, selectedModule.deps);
            }
        }

        setModules(updatedModules);
        setToggleAllPermissions(isAllPermissionsSelected(updatedModules));
    }

    const getInvalidModulePermissions = (modules) => {
        const invalidModules = [];
        if (Array.isArray(modules)) {
            modules.forEach((m) => {
                const viewAction = m.permissions.find((a) => a.key === "VIEW");
                if (viewAction?.selected && Array.isArray(m.deps)) {
                    m.deps.forEach((d) => {
                        const dm = modules.find((m) => m.key === d);
                        if (dm) {
                            const va = dm.permissions.find((a) => a.key === "VIEW");
                            if (!va?.selected) {
                                const im = invalidModules.find((im) => im.key === m.key);
                                if (im) {
                                    im.deps = [...im.deps, dm.name];
                                } else {
                                    invalidModules.push({
                                        key: m.key,
                                        name: m.name,
                                        deps: [dm.name]
                                    });
                                }
                            }
                        }
                    });
                }
            });
        }

        return invalidModules;
    }

    const isAllPermissionsSelected = (modules) => {
        let isAllPermissionsSelected = true;

        modules.forEach((module) => {
            module.permissions.forEach((action) => {
                if (action.key !== "SELECT_ALL" && !action.selected) {
                    isAllPermissionsSelected = false;
                }
            });
        });

        return isAllPermissionsSelected;
    }

    const getPayload = () => {
        const payload = {
            name: nameInput.value,
            accessToAllModules: isAllPermissionsSelected(modules),
            modules: modules.map((module) => {
                const permissions = module.permissions.filter((a) => a.key !== "SELECT_ALL" && a.selected).map((a) => a.key);

                return {
                    key: module.key,
                    name: module.name,
                    permissions: permissions
                };
            })
        }

        if (role) {
            payload["id"] = role.id;
        }

        return payload;
    }

    const saveRole = () => {
        const invalidModules = getInvalidModulePermissions(modules);
        if (invalidModules.length > 0) {
            showAlert(
                "Alert",
                `Please provide below module view permission in order to work provided permission properly.
                <br \>
                <br \>
                ${invalidModules.map((im) => `${im.name}: ${im.deps.join(', ')}`).join('<br \>')}`
            );
            return;
        }

        if (isEmpty(nameInput.value)) {
            setNameInput({
                ...nameInput,
                canValidate: true
            });
            return;
        }

        const payload = getPayload();
        if (role) {
            showLoader("Updating Role... Please wait");
            UserPermissionService.updateRole(payload).then(
                (response) => {
                    removeLoader();
                    showToast("Updated role successfully.", TOAST_TYPE.SUCCESS);
                    props.onClose(true);
                }, (error) => {
                    removeLoader();
                    if (error?.message) {
                        showToast(error.message, TOAST_TYPE.FAILURE);
                    } else {
                        showToast("Error while updating role, Please try after sometime.", TOAST_TYPE.FAILURE);
                    }
                }
            );
        } else {
            showLoader("Adding Role... Please wait");
            UserPermissionService.saveRole(payload).then(
                (response) => {
                    removeLoader();
                    showToast("Added role successfully.", TOAST_TYPE.SUCCESS);
                    props.onClose(true);
                    props.onRoleCreate(response)
                }, (error) => {
                    removeLoader();
                    if (error?.message) {
                        showToast(error.message, TOAST_TYPE.FAILURE);
                    } else {
                        showToast("Error while updating role, Please try after sometime.", TOAST_TYPE.FAILURE);
                    }
                }
            );
        }
    }

    return (
        <div className="transparent-background">
            <div className="popup-window" style={{ padding: 0, overflow: "hidden", width: "40vw", boxShadow: "none", maxHeight:'100%' }}>
                <div className="parent-width" style={{ height: "72vh" }}>
                    <div className="p-h-xl pt-xl pb-m">
                        <div className="row">
                            <div>
                                <DKLabel text={role ? t("Edit Role") : t("Add Role")} className="fs-l fw-b" />
                                <DKLabel text="Apply module level permissions specific to this role" className="mt-xs text-gray" />
                            </div>
                            <div className="ml-auto d-flex">
                                <DKButton
                                    title={t("CANCEL")}
                                    className="border-radius-m bg-gray1 border-m"
                                    onClick={() => {
                                        props.onClose();
                                    }}
                                />
                                <DKButton
                                    title={t("SAVE")}
                                    className="bg-app text-white ml-m"
                                    disabled={role?.isDefault || role?.isSystemRole}
                                    onClick={() => {
                                        if (role?.isDefault || role?.isSystemRole) {
                                            showAlert("Operation not allowed!", "You can not edit the default role.");
                                            return;
                                        }

                                        saveRole();
                                    }}
                                />
                            </div>
                        </div>
                        <div className="row parent-width mt-l">
                            <DKInput
                                title={nameInput.title}
                                required={nameInput.required}
                                direction={INPUT_VIEW_DIRECTION.VERTICAL}
                                value={nameInput.value}
                                errorMessage={" "}
                                readOnly={role?.isDefault || role?.isSystemRole}
                                canValidate={nameInput.canValidate}
                                onChange={(value) => {
                                    setNameInput({
                                        ...nameInput,
                                        canValidate: true,
                                        value: value
                                    });
                                }}
                            />
                        </div>
                        <div className="row parent-width mt-l justify-content-between">
                            <DKCheckMark
                                title={toggleAllPermissions ? "Reset access to all modules" : "Provide access to all modules"}
                                className="fw-m"
                                isSelected={toggleAllPermissions}
                                color={"bg-button"}
                                onClick={(e) => {
                                    e?.stopPropagation();
                                    if (role?.isDefault || role?.isSystemRole) {
                                        showAlert("Operation not allowed!", "You can not edit the default role.");
                                        return;
                                    }

                                    onAllPermissionToggle(!toggleAllPermissions);
                                    setToggleAllPermissions(!toggleAllPermissions);
                                }}
                            />
                        </div>
                    </div>
                    <div className="parent-width" style={{ overflow: "auto", height: "calc(100% - 194px)" }}>
                        <div className="parent-width parent-height p-h-xl p-v-l row align-items-stretch">
                            <div className=" z-index-1 custom-scroll mb-xl pb-l" style={{ width: 200, paddingRight: 2, overflowY: "auto" }}>
                                {modules?.map((module) => {
                                    return (
                                        <div
                                            className="row justify-content-between cursor-hand"
                                            onClick={() => {
                                                onModuleSelect(module);
                                            }}
                                        >
                                            <DKLabel
                                                text={module.name}
                                                className={`parent-width p-v-s ${module.selected && "fw-m text-app"}`}
                                                style={{ backgroundColor: module.selected && "#f4f4f6" }}
                                            />
                                            {module.selected && <div className="bg-button parent-height" style={{ width: 2, height: 30 }} />}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="parent-height bg-gray2 column" style={{ width: 1, marginLeft: -2 }} />
                            <div className="z-index-1 flex-1 custom-scroll" style={{ paddingLeft: 16, paddingRight: 2, overflowY: "auto" }}>
                                {modules?.find((m) => m.selected)?.permissions?.map((action) => {
                                    return (
                                        <div
                                            className={`p-v-s row justify-content-between ${role?.isDefault || role?.isSystemRole ? "" : "cursor-hand"}`}
                                            style={{
                                                paddingLeft: 2,
                                                paddingRight: 2,
                                            }}
                                            onClick={(e) => {
                                                e?.stopPropagation();
                                                if (role?.isDefault || role?.isSystemRole) {
                                                    showAlert("Operation not allowed!", "You can not edit the default role.");
                                                    return;
                                                }

                                                onPermissionToggle(action);
                                            }}
                                        >   
                                            {action.key === "SELECT_ALL" && <DKLabel text={`${action.selected ? "Unselect All" : "Select All"}`} className="fw-m text-capitalize" />}
                                            {action.key !== "SELECT_ALL" && <DKLabel text={action.title} />}
                                            <DKCheckMark
                                                isSelected={action.selected}
                                                color={"bg-button"}
                                                onClick={(e) => {
                                                    e?.stopPropagation();
                                                    if (role?.isDefault || role?.isSystemRole) {
                                                        showAlert("Operation not allowed!", "You can not edit the default role.");
                                                        return;
                                                    }

                                                    onPermissionToggle(action);
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CreateRole;
